import React from 'react';
import { Box, Chip, Link, Stack, Tooltip, Typography } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { MESSAGE_ERROR_REFERENCE_URL } from 'src/constants/links';
import { Panel } from 'src/UIKit';
import { GetMessageErrorSummaryReportsResponse } from '../../api/types';

interface ReportsSummaryProps {
  messageErrorSummaryReports: GetMessageErrorSummaryReportsResponse | null;
}

export const ReportsSummary: React.FC<ReportsSummaryProps> = ({ messageErrorSummaryReports }) => {
  const errorCodeSummary = messageErrorSummaryReports || [];
  const totalErrors = errorCodeSummary.reduce((sum, item) => sum + (item.totalErrorCount || 0), 0);
  const totalMessages = errorCodeSummary[0]?.totalMessages ?? 0;
  const statistics = [
    {
      title: 'Total Errors',
      value: totalErrors.toLocaleString(),
      caption: `Across ${errorCodeSummary.length} error types`,
    },
    {
      title: 'Total Messages',
      value: totalMessages.toLocaleString(),
      caption: 'Across all companies',
    },
    {
      title: 'Error Rate',
      value: `${totalMessages > 0 ? ((totalErrors / totalMessages) * 100).toFixed(2) : '0'}%`,
      caption: 'Overall percentage',
    },
  ];

  return (
    <Stack height="100%" gap={1}>
      {statistics.map(({ title, value, caption }) => (
        <Panel key={title}>
          <Typography color="textSecondary" variant="h6">
            {title}
          </Typography>
          <Typography variant="h5" component="div">
            {value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {caption}
          </Typography>
        </Panel>
      ))}
      <Panel sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 50 }}>
        <Stack alignItems="flex-start" gap={0.25} mb={1}>
          <Typography color="textSecondary" variant="h6">
            Error Code Summary
          </Typography>
          <Link
            href={MESSAGE_ERROR_REFERENCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
          >
            Delivery error reference <OpenInNewIcon aria-hidden fontSize="inherit" />
          </Link>
        </Stack>
        <Stack gap={1} overflow="auto" minHeight={0}>
          {errorCodeSummary.length > 0 ? (
            errorCodeSummary.map((error, index) => (
              <Box key={index}>
                <Tooltip
                  title={
                    <>
                      <Typography variant="body2" component="div" fontWeight="bold">
                        {error.errorTitle}
                      </Typography>
                      <Typography variant="body2" component="div">
                        {error.errorDetail}
                      </Typography>
                    </>
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={`${error.errorCode || 'Unknown'}: ${
                      error.totalErrorCount?.toLocaleString() || 0
                    } errors`}
                    variant="outlined"
                    size="small"
                    color={error.errorCode ? 'primary' : 'secondary'}
                  />
                </Tooltip>
              </Box>
            ))
          ) : (
            <Typography color="textSecondary" align="center">
              No error data available.
            </Typography>
          )}
        </Stack>
      </Panel>
    </Stack>
  );
};
